export enum SketchTool {
  Select = 'select',
  Pan = 'pan',
  Pen = 'pen',
  Highlighter = 'highlighter',
  Eraser = 'eraser',
  Line = 'line',
  Arrow = 'arrow',
  Rect = 'rect',
  Ellipse = 'ellipse',
  Text = 'text',
  Marker = 'marker',
  Measure = 'measure',
  Symbol = 'symbol',
}

export enum SketchKind {
  Page = 'page',
  Background = 'background',
  Ink = 'ink',
  Highlight = 'highlight',
  Shape = 'shape',
  Text = 'text',
  Marker = 'marker',
  Measure = 'measure',
  Symbol = 'symbol',
}

export interface InkColor {
  readonly name: string;
  readonly hex: string;
}

export const INK_COLORS: readonly InkColor[] = [
  { name: 'Black', hex: '#111111' },
  { name: 'Blue', hex: '#1F4FC9' },
  { name: 'Red', hex: '#C62828' },
  { name: 'Green', hex: '#2E7D32' },
  { name: 'Orange', hex: '#EF6C00' },
  { name: 'Purple', hex: '#6A1B9A' },
  { name: 'Grey', hex: '#6B7280' },
  { name: 'White', hex: '#FFFFFF' },
];

export const HIGHLIGHT_COLORS: readonly InkColor[] = [
  { name: 'Yellow', hex: '#FFE400' },
  { name: 'Green', hex: '#8BF55A' },
  { name: 'Pink', hex: '#FF7AC8' },
  { name: 'Blue', hex: '#6EC6FF' },
];

export const PEN_SIZES: readonly number[] = [2, 4, 6, 10];
export const HIGHLIGHTER_SIZE = 22;
export const HIGHLIGHTER_OPACITY = 0.4;
export const SHAPE_STROKE_WIDTH = 3;
export const TEXT_FONT_SIZE = 18;
export const TEXT_FONT_FAMILY = 'Arial, Helvetica, sans-serif';
export const TEXT_BOX_WIDTH = 220;
export const MARKER_RADIUS = 15;
export const MARKER_FONT_SIZE = 15;
export const MEASURE_FONT_SIZE = 13;
export const SYMBOL_DEFAULT_SIZE = 90;
export const ARROW_HEAD_LENGTH = 14;
export const ARROW_HEAD_WIDTH = 10;
export const MIN_SHAPE_SIZE = 3;
export const MIN_ZOOM = 0.1;
export const MAX_ZOOM = 8;
export const ZOOM_STEP = 1.2;
export const FIT_MARGIN_PX = 32;
export const WHEEL_ZOOM_SENSITIVITY = 0.0015;
export const TARGET_FIND_TOLERANCE_PX = 6;
export const MAX_TITLE_LENGTH = 120;
export const MAX_DESCRIPTION_LENGTH = 2000;
export const MAX_MARKER_LABEL_LENGTH = 120;
export const EXPORT_MAX_EDGE_PX = 2400;
export const EXPORT_BACKGROUND = '#FFFFFF';
export const WORKSPACE_BACKGROUND = '#DDE3EC';
export const DEFAULT_INK_COLOR = INK_COLORS[0].hex;
export const DEFAULT_HIGHLIGHT_COLOR = HIGHLIGHT_COLORS[0].hex;
export const DEFAULT_PEN_SIZE = PEN_SIZES[1];
export const DEFAULT_SHAPE_COLOR = INK_COLORS[1].hex;
export const DEFAULT_MARKER_COLOR = INK_COLORS[2].hex;
export const DEFAULT_MEASURE_COLOR = INK_COLORS[3].hex;

export const CUSTOM_OBJECT_PROPS: readonly string[] = [
  'fnKind',
  'fnInk',
  'fnInkSize',
  'fnInkKind',
  'fnNumber',
  'fnLabel',
  'fnPixels',
  'fnReference',
  'fnLine',
  'fnColor',
  'fnWidth',
  'fnSymbolId',
  'selectable',
  'evented',
  'perPixelTargetFind',
];

export const TOOL_SHORTCUTS: Readonly<Record<string, SketchTool>> = {
  v: SketchTool.Select,
  h: SketchTool.Pan,
  p: SketchTool.Pen,
  b: SketchTool.Pen,
  g: SketchTool.Highlighter,
  e: SketchTool.Eraser,
  l: SketchTool.Line,
  a: SketchTool.Arrow,
  r: SketchTool.Rect,
  o: SketchTool.Ellipse,
  t: SketchTool.Text,
  m: SketchTool.Marker,
  d: SketchTool.Measure,
  s: SketchTool.Symbol,
};

export interface ToolDescriptor {
  readonly tool: SketchTool;
  readonly label: string;
  readonly icon: string;
  readonly key: string;
  readonly hint: string;
}

export const TOOL_DESCRIPTORS: readonly ToolDescriptor[] = [
  { tool: SketchTool.Select, label: 'Select', icon: 'near_me', key: 'V', hint: 'Select, move, resize and rotate anything on the page' },
  { tool: SketchTool.Pan, label: 'Pan', icon: 'pan_tool', key: 'H', hint: 'Drag to move around the page. Two fingers also pan and pinch-zoom' },
  { tool: SketchTool.Pen, label: 'Pen', icon: 'edit', key: 'P', hint: 'Pressure-sensitive ink for handwriting and freehand sketching' },
  { tool: SketchTool.Highlighter, label: 'Highlighter', icon: 'border_color', key: 'G', hint: 'Translucent highlighter' },
  { tool: SketchTool.Eraser, label: 'Eraser', icon: 'auto_fix_normal', key: 'E', hint: 'Drag across a stroke or object to remove it' },
  { tool: SketchTool.Line, label: 'Line', icon: 'remove', key: 'L', hint: 'Straight line. Hold Shift to snap the angle' },
  { tool: SketchTool.Arrow, label: 'Arrow', icon: 'north_east', key: 'A', hint: 'Arrow pointing where you release' },
  { tool: SketchTool.Rect, label: 'Rectangle', icon: 'crop_square', key: 'R', hint: 'Rectangle outline. Hold Shift for a square' },
  { tool: SketchTool.Ellipse, label: 'Ellipse', icon: 'circle', key: 'O', hint: 'Ellipse outline. Hold Shift for a circle' },
  { tool: SketchTool.Text, label: 'Text', icon: 'title', key: 'T', hint: 'Tap the page to type a label' },
  { tool: SketchTool.Marker, label: 'Evidence marker', icon: 'pin_drop', key: 'M', hint: 'Numbered evidence marker. Numbers are never reused' },
  { tool: SketchTool.Measure, label: 'Measure', icon: 'straighten', key: 'D', hint: 'Draw a line between two points. The first one sets the scale' },
  { tool: SketchTool.Symbol, label: 'Symbols', icon: 'category', key: 'S', hint: 'Doors, furniture, vehicles, bodies, north arrow and more' },
];

export const DRAWING_TOOLS: ReadonlySet<SketchTool> = new Set([
  SketchTool.Pen,
  SketchTool.Highlighter,
  SketchTool.Eraser,
  SketchTool.Line,
  SketchTool.Arrow,
  SketchTool.Rect,
  SketchTool.Ellipse,
  SketchTool.Measure,
]);

export function isDrawingTool(tool: SketchTool): boolean {
  return DRAWING_TOOLS.has(tool);
}

export function toolForKey(key: string): SketchTool | null {
  const tool = TOOL_SHORTCUTS[key.toLowerCase()];
  return tool === undefined ? null : tool;
}
